import React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import type { Locale } from "date-fns";
import { CalendarDays, Cpu, Tag, UserRound } from "lucide-react";

import useAuthStore from "@/store/use-auth-store";

import { TicketStatusMenu } from "./TicketStatusMenu";
import { isOwnTicket } from "../utils/message-author";
import type { SupportTicket } from "../types";

const MetaChip: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-2 py-1 sm:px-2.5 sm:py-1.5">
    <span className="shrink-0 text-muted-foreground">{icon}</span>
    {/* Dar ekranda etiket yerine yalnızca değer gösterilir; satır sayısı azalır */}
    <span className="hidden text-xs text-muted-foreground sm:inline">
      {label}
    </span>
    <span className="max-w-[120px] truncate text-xs font-medium text-foreground sm:max-w-[180px]">
      {value}
    </span>
  </div>
);

interface TicketDetailHeaderProps {
  ticket: SupportTicket;
  /** Mesajlardan türetilen model bilgisi */
  model?: string | null;
  dateLocale: Locale;
}

/**
 * Ticket başlığı — numara rozeti, konu, durum ve bağlam çipleri.
 */
export const TicketDetailHeader: React.FC<TicketDetailHeaderProps> = ({
  ticket,
  model,
  dateLocale,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  // Admin başkasının ticket'ına bakarken talebin kime ait olduğunu görmeli
  const showRequester = !isOwnTicket(ticket, user);

  return (
    <div className="relative overflow-hidden border-b border-border">
      {/* Yumuşak renk geçişi — başlığı konuşmadan görsel olarak ayırır */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-500/[0.07] via-violet-500/[0.04] to-transparent dark:from-indigo-500/[0.12] dark:via-violet-500/[0.06]" />

      <div className="relative flex flex-col gap-2.5 px-3 py-3 sm:gap-4 sm:px-6 sm:py-5">
        <div className="flex items-start justify-between gap-3 sm:gap-4">
          <div className="flex min-w-0 flex-col gap-1 sm:gap-2">
            {typeof ticket.ticketNumber === "number" && (
              <span className="w-fit rounded-md bg-indigo-500/10 px-2 py-0.5 font-mono text-xs font-semibold text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400">
                #{ticket.ticketNumber}
              </span>
            )}
            <h1 className="truncate text-base font-semibold leading-snug tracking-tight text-foreground sm:text-lg sm:leading-snug md:text-xl">
              {ticket.subject || t("support.myTickets.untitled")}
            </h1>
          </div>

          {/* Admin için tıklanabilir durum menüsü, diğerlerine düz rozet */}
          <TicketStatusMenu className="mt-0.5 shrink-0" />
        </div>

        {/* Bağlam çipleri */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          {showRequester && (
            <MetaChip
              icon={<UserRound className="h-3.5 w-3.5" />}
              label={t("support.myTickets.requesterLabel")}
              value={
                ticket.fullName
                  ? `${ticket.fullName} · ${ticket.email}`
                  : ticket.email
              }
            />
          )}
          {model && (
            <MetaChip
              icon={<Cpu className="h-3.5 w-3.5" />}
              label={t("support.form.model")}
              value={model}
            />
          )}
          {ticket.category && (
            <MetaChip
              icon={<Tag className="h-3.5 w-3.5" />}
              label={t("support.myTickets.categoryLabel")}
              value={t(`support.backendCategories.${ticket.category}`, {
                defaultValue: ticket.category,
              })}
            />
          )}
          <MetaChip
            icon={<CalendarDays className="h-3.5 w-3.5" />}
            label={t("support.myTickets.createdLabel")}
            value={format(new Date(ticket.createdAt), "d MMM yyyy", {
              locale: dateLocale,
            })}
          />
        </div>
      </div>
    </div>
  );
};
